/* eslint-disable max-lines */
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { upperFirst } from 'lodash-es';
import { Subject, forkJoin, lastValueFrom } from 'rxjs';
import { distinctUntilChanged, map, switchMap, takeUntil } from 'rxjs/operators';

import { Company, CompanyBilling } from '@site-mate/dashpivot-shared-library';
import {
  BillingCycles,
  CompanyPlanTypes,
  IAccount,
  SubscriptionStatuses,
  SubscriptionTerms,
} from '@site-mate/sitemate-global-shared';

import { GlobalApiService } from 'app/global-api/global-api.service';
import { ICompanyHomeSettings } from 'app/home/company-home-settings.model';
import { TmpI18NService } from 'app/i18n/tmp-i18n.service';
import { IntercomService } from 'app/intercom/intercom.service';
import { CompanyService } from 'app/shared/service/company.service';
import { ErrorHandler } from 'app/shared/service/error-handler.service';
import { LastVisitedHomeService } from 'app/shared/service/last-visited-home.service';
import { ToastrService } from 'app/shared/service/toastr.service';
import {
  IDashpivotBillingSettingsForm,
  WorkspaceBillingService,
} from 'app/shared/service/workspace-billing.service';
import { UserService } from 'app/user/user.service';

type EnhancedFormModel = IDashpivotBillingSettingsForm & Partial<ICompanyHomeSettings>;

@Component({
  selector: 'cc-workspace-sms-dashpivot-billing-settings',
  templateUrl: './workspace-sms-dashpivot-billing-settings.component.html',
  styleUrls: ['./workspace-sms-dashpivot-billing-settings.component.scss'],
})
export class WorkspaceSmsDashpivotBillingSettingsComponent implements OnInit, OnDestroy {
  isSuperAdmin: boolean;
  isLoading = true;
  isSaving = false;
  company: Company;
  account: IAccount;
  groupBillingTooltip: string;
  legacyRestrictionsTooltip: string;

  formModel: EnhancedFormModel;

  @ViewChild('form') ngBillingForm: NgForm;

  private unsubscribe = new Subject<void>();

  readonly companyPlanTypes = CompanyPlanTypes;
  readonly planTypeSelectorValues = this.getSelectorOptions(CompanyPlanTypes);
  readonly subscriptionStatusSelectorValues = this.getSelectorOptions(SubscriptionStatuses);
  readonly subscriptionTermSelectorValues = this.getSelectorOptions(SubscriptionTerms);
  readonly billingCycleSelectorValues = this.getSelectorOptions(BillingCycles);

  constructor(
    private readonly activatedRoute: ActivatedRoute,
    private readonly userService: UserService,
    private readonly companyService: CompanyService,
    private readonly errorHandler: ErrorHandler,
    private readonly toastrService: ToastrService,
    private readonly i18nService: TmpI18NService,
    private readonly intercomService: IntercomService,
    private readonly globalApiService: GlobalApiService,
    private readonly workspaceBillingService: WorkspaceBillingService,
    private readonly lastVisitedHomeService: LastVisitedHomeService,
    private readonly router: Router,
  ) {}

  ngOnInit() {
    this.setupModel();

    this.groupBillingTooltip = this.i18nService.getMessage('groupBillingTooltip');
    this.legacyRestrictionsTooltip = this.i18nService.getMessage('legacyRestrictionsTooltip');
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  groupBillingToggleClicked() {
    this.formModel.groupBilling = !this.formModel.groupBilling;
  }

  legacyRestrictionToggleClicked() {
    this.formModel.dashpivotLegacyPlanRestrictionsEnabled =
      !this.formModel.dashpivotLegacyPlanRestrictionsEnabled;
  }

  canDisplayDatePicker(event: Event) {
    if (!this.isSuperAdmin) {
      event.preventDefault();
    }

    return this.isSuperAdmin;
  }

  setupModel(): void {
    this.isSuperAdmin = this.userService.isAdmin();
    this.activatedRoute.params
      .pipe(
        distinctUntilChanged(),
        switchMap((params) => this.companyService.getCompany(params.companyId)),
        switchMap((company) =>
          this.globalApiService.getAccount(company.accountId).pipe(map((account) => ({ company, account }))),
        ),
        takeUntil(this.unsubscribe),
      )
      .subscribe({
        next: ({ company, account }) => {
          this.company = company;
          this.account = account;
          const legacySettings = this.companyService.getCompanyModel(company);
          const formModel = this.workspaceBillingService.buildDashpivotBillingSettingsForm(
            account,
            legacySettings,
          );
          this.formModel = this.enhanceFormModel(formModel, legacySettings);
          this.isLoading = false;
        },
        error: (error) => {
          this.errorHandler.handle(error);
          this.lastVisitedHomeService
            .goToFirstPossibleTeam(this.router, true)
            .pipe(takeUntil(this.unsubscribe));
        },
      });
  }

  get pricingInputs() {
    return {
      currency: this.formModel.paymentCurrency,
      term: this.formModel.dashpivotSubscriptionTerm,
      plan: this.formModel.dashpivotPlanType,
    };
  }

  async onSubmit(event: Event): Promise<void> {
    event.preventDefault();

    if (!this.canSaveSettings) {
      return;
    }

    this.isSaving = true;
    await this.updateBilling();
  }

  private resetForm(): void {
    this.ngBillingForm.resetForm(this.ngBillingForm.value);
  }

  openLiveChat() {
    this.intercomService.open();
  }

  // TODO: This function should be unnecessary once we stop writing billing settings to DP DB + Billing Company Licenses component has been refactored
  /**
   * This function is needed as we need to write to both DP and SMS databases when the form is saved.
   * Company Billing Licenses component is tightly coupled with the old billing settings model
   * and it requires the old billing settings model to be passed as an input.
   * This function enhances the form model with the old billing settings model and makes it
   * backward-compatible with the Company Billing Licenses component.
   *
   * We cannot just simply change the model in Billing Company Licenses component to use the new form model
   * because the "rates" need to be sent to DP API while SMS API doesn't need it.
   */
  private enhanceFormModel(
    formModel: IDashpivotBillingSettingsForm,
    legacySettings: ICompanyHomeSettings,
  ): IDashpivotBillingSettingsForm {
    const {
      sitemateFreeUsersLicensesRate,
      visitorsLicensesRate,
      contributorsLicensesRate,
      sitematePaidUsersLicensesRate,
      id: companyId,
    }: ICompanyHomeSettings = legacySettings;
    const { sitematePaidUsersPaid, contributorUsersPaid, ..._formModel } = formModel;

    const enhancedFormModel: EnhancedFormModel = {
      // Below are the properties that are required by the
      // Company Billing Licenses component to work properly.
      id: companyId,
      sitemateFreeUsersLicensesRate,
      visitorsLicensesRate,
      contributorsLicensesRate,
      sitematePaidUsersLicensesRate,
      paymentCurrency: this.account?.billing?.currency,
      get planType() {
        return this.dashpivotPlanType;
      },
      get subscriptionTerm() {
        return this.dashpivotSubscriptionTerm;
      },
      sitematePaidUsersLicenses: sitematePaidUsersPaid,
      contributorsLicenses: contributorUsersPaid,

      // And below are the SMS settings.
      // The getters below are needed for mapping to SMS Account
      // and we rely on the Billing Company Licenses component to
      // update the form model with the old property names.
      get sitematePaidUsersPaid() {
        return this.sitematePaidUsersLicenses;
      },
      get contributorUsersPaid() {
        return this.contributorsLicenses;
      },
      ..._formModel,
    };

    return enhancedFormModel;
  }

  private mapFormToLegacyBillingSettings(form: IDashpivotBillingSettingsForm) {
    const legacyBillingSettings: ICompanyHomeSettings = {
      ...this.companyService.getCompanyModel(this.company),

      trialExpiration: form.dashpivotTrialEndDate,
      planType: form.dashpivotPlanType,
      subscriptionStatus: form.dashpivotSubscriptionStatus,
      subscriptionInitialDate: form.dashpivotInitialSubscriptionDate,
      subscriptionTerm: form.dashpivotSubscriptionTerm,
      billingCycle: form.dashpivotBillingCycle,
      groupBilling: form.groupBilling,
      legacyRestrictions: form.dashpivotLegacyPlanRestrictionsEnabled,
      contributorsLicenses: form.contributorUsersPaid,
      sitematePaidUsersLicenses: form.sitematePaidUsersPaid,

      // TODO: Remove these properties once we stop writing to DP DB (Legacy)
      sitemateFreeUsersLicensesRate: this.formModel.sitemateFreeUsersLicensesRate,
      visitorsLicensesRate: this.formModel.visitorsLicensesRate,
      contributorsLicensesRate: this.formModel.contributorsLicensesRate,
      sitematePaidUsersLicensesRate: this.formModel.sitematePaidUsersLicensesRate,
    };

    return legacyBillingSettings;
  }

  private updateLegacyBillingSettings() {
    const updatedLegacyBillingSettings: ICompanyHomeSettings = this.mapFormToLegacyBillingSettings(
      this.formModel,
    );
    const legacyCompanyBilling: CompanyBilling = this.workspaceBillingService.buildLegacyCompanyBilling(
      updatedLegacyBillingSettings,
    );

    return this.companyService.updateCompanyBilling(this.company.id, legacyCompanyBilling);
  }

  private updateDashpivotBillingSettings() {
    const dashpivotTrialEndDate = this.formModel.dashpivotTrialEndDate
      ? this.workspaceBillingService.convertToISODate(this.formModel.dashpivotTrialEndDate)
      : null;
    const dashpivotInitialSubscriptionDate = this.formModel.dashpivotInitialSubscriptionDate
      ? this.workspaceBillingService.convertToISODate(this.formModel.dashpivotInitialSubscriptionDate)
      : null;

    return this.globalApiService.updateDashpivotBillingSettings(this.account._id, {
      trialEndDate: dashpivotTrialEndDate,
      planType: this.formModel.dashpivotPlanType,
      subscriptionStatus: this.formModel.dashpivotSubscriptionStatus,
      initialSubscriptionDate: dashpivotInitialSubscriptionDate,
      subscriptionTerm: this.formModel.dashpivotSubscriptionTerm,
      billingCycle: this.formModel.dashpivotBillingCycle,
      legacyPlanRestrictionsEnabled: this.formModel.dashpivotLegacyPlanRestrictionsEnabled,
      contributorUsersPaid: this.formModel.contributorUsersPaid,
      sitematePaidUsersPaid: this.formModel.sitematePaidUsersPaid,
    });
  }

  private async updateBilling(): Promise<void> {
    try {
      await lastValueFrom(
        forkJoin([
          // Write to DP Database
          this.updateLegacyBillingSettings(),
          // Write to Global Database
          this.updateDashpivotBillingSettings(),
        ]),
      );

      this.setupModel();
      this.toastrService.successByKey('sitemateStartSettingsUpdateSuccess');
      this.resetForm();
    } catch (error) {
      // @ts-expect-error implicitly has an 'any' type.
      error.toastKey = 'sitemateStartSettingsUpdateFailed';
      this.errorHandler.handle(error);
    } finally {
      this.isSaving = false;
    }
  }

  get canSaveSettings() {
    const hasFormChanged =
      this.ngBillingForm?.form.dirty || this.hasLicensesInputsChanged || this.hasInputTogglesChanged;

    return this.isSuperAdmin && hasFormChanged;
  }

  get hasLicensesInputsChanged(): boolean {
    if (!this.formModel) {
      return false;
    }

    // These properties are set by the Billing Company Licenses component
    const { contributorsLicenses, sitematePaidUsersLicenses } = this.formModel;
    // We want to compare to what's fetched from the backend
    const { contributorUsersPaid, sitematePaidUsersPaid } = this.account?.billing?.dashpivot ?? {};

    const hasDashpivotUsersChanged = contributorsLicenses !== contributorUsersPaid;
    const hasSitemateUsersChanged = sitematePaidUsersLicenses !== sitematePaidUsersPaid;

    return hasDashpivotUsersChanged || hasSitemateUsersChanged;
  }

  get hasInputTogglesChanged(): boolean {
    if (!this.company?.billing) {
      return false;
    }

    const hasLegacyRestrictionsChanged =
      this.formModel.dashpivotLegacyPlanRestrictionsEnabled !==
      this.account.billing?.dashpivot?.legacyPlanRestrictionsEnabled;
    const hasGroupBillingChanged = this.formModel.groupBilling !== this.company.billing?.groupBilling;

    return hasLegacyRestrictionsChanged || hasGroupBillingChanged;
  }

  private getSelectorOptions(options: any) {
    return Object.values(options).map((option) => {
      return { label: upperFirst(option), value: option };
    });
  }
}
